import { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function AuthCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const { exchangeSession } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    // Use useRef to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      try {
        // Extract session_id from URL fragment
        const hash = window.location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (!sessionIdMatch) {
          throw new Error('No session_id found in URL');
        }

        const sessionId = sessionIdMatch[1];
        
        // Exchange session_id for user data
        const userData = await exchangeSession(sessionId);
        
        // Clear the hash and navigate
        window.history.replaceState(null, '', window.location.pathname);
        
        // Navigate based on onboarding status
        if (userData.onboarded) {
          navigate('/dashboard', { replace: true, state: { user: userData } });
        } else {
          navigate('/onboarding', { replace: true, state: { user: userData } });
        }
      } catch (err) {
        console.error('Auth callback error:', err);
        setError(err.message);
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 2000);
      }
    };

    processSession();
  }, [exchangeSession, navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#0F1224] flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-500/20 flex items-center justify-center">
            <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <p className="text-white font-medium mb-2">Authentication Failed</p>
          <p className="text-[#9CA3AF] text-sm">{error}</p>
          <p className="text-[#6B7280] text-xs mt-2">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F1224] flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 mx-auto mb-4 border-4 border-[#63E6E2]/30 border-t-[#63E6E2] rounded-full animate-spin" />
        <p className="text-white font-medium">Signing you in...</p>
        <p className="text-[#9CA3AF] text-sm mt-1">Please wait</p>
      </div>
    </div>
  );
}
